"use client";

import React, { useState, useCallback } from "react";
import Input from "../components/ui/form/Input";
import TextArea from "../components/ui/form/TextArea";
import Button from "../../../ui/Button";
import Modal from "./ui/Modal";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";
import { ParentCard } from "../PropertyPage1";

const formSchema = z.object({
  name: z.string().min(1, { message: "Full name is required" }),
  link: z.string().min(1, { message: "Full name is required" }),
  phone: z
    .string()
    .min(10, { message: "Phone number must be at least 10 digits" })
    .regex(/^\d+$/, { message: "Phone number must contain only digits" }),

  email: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email address" }),
  message: z
    .string()
    .min(10, { message: "Message must be at least 10 characters" }),
  subject: z.string().min(1, { message: "Subject is required" }),
  to: z.string().email(),
});

type FormData = z.infer<typeof formSchema>;

const FORM_SUBMITTED_KEY = "mandateFormSubmitted";

const ContactMandate: React.FC<{ email: string }> = ({ email }) => {
  return (
    <ParentCard header="Contact Mandate" className="!overflow-visible">
      <Form email={email} />
    </ParentCard>
  );
};

export const Form: React.FC<{
  variant?: boolean;
  email: string;
}> = ({ variant, email }) => {
  const currentUrl = () => {
    if (typeof window !== "undefined") {
      // ensure we're in the browser
      const fullUrl = window.location.href;
      return fullUrl;
    }
    return ""; // fallback for server-side
  };



  const link = currentUrl();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
    phone: "",
    subject: "Potential Client – Property Inquiry",
    to: email,
    link,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>(
    {}
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState<"success" | "error">("success");

  // useEffect(() => {
  //   if (localStorage.getItem(FORM_SUBMITTED_KEY)) {
  //     setModalMessage("You have already submitted the form.");
  //     setModalType("error");
  //     setIsModalOpen(true);
  //   }
  // }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BASE_URL}api/v1/contact/company`, // Update if needed
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );
      if (!response.ok) throw new Error("Failed to submit form");
      return response.json();
    },
    onSuccess: () => {
      localStorage.setItem(FORM_SUBMITTED_KEY, "true");
      setModalMessage("Form submitted successfully!");
      setModalType("success");
      setIsModalOpen(true);
      setFormData({
        name: "",
        email: "",
        message: "",
        phone: "",
        subject: "",
        to: email,
        link: "",
      });
      setErrors({});
    },
    onError: (error: Error) => {
      setModalMessage(error.message || "Something went wrong.");
      setModalType("error");
      setIsModalOpen(true);
    },
  });

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (localStorage.getItem(FORM_SUBMITTED_KEY)) {
        setModalMessage("You have already submitted the form.");
        setModalType("error");
        setIsModalOpen(true);
        return;
      }

      try {
        const validatedData = formSchema.parse(formData);
        mutate(validatedData);
      } catch (error) {

        if (error instanceof z.ZodError) {
          const newErrors: Partial<Record<keyof FormData, string>> = {};
          error.errors.forEach((err) => {
            if (err.path[0]) {
              newErrors[err.path[0] as keyof FormData] = err.message;
            }
          });
          setErrors(newErrors);
        }
      }
    },
    [formData, mutate]
  );

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-2">
        <Input
          label="Full Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter full name"
          error={errors.name}
        />
        <Input
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter email address"
          error={errors.email}
        />
        <Input
          label="Phone Number"
          name="phone"
          inputMode="numeric"
          pattern="0[7-9][01]\d{8}"
          maxLength={11}
          value={formData.phone}
          onChange={handleChange}
          placeholder="Enter phone number"
          error={errors.email}
        />
        <TextArea
          label="Write about your interest"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Write here..."
          error={errors.message}
        />
        <Button
          className={`w-full primary-btn block ${variant && "!mt-7"}`}
          type="submit"
          disabled={isPending}
        >
          {isPending ? "Submitting..." : "Submit"}
        </Button>
      </form>

      {isModalOpen && (
        <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
          <div
            className={`text-center ${
              modalType === "success" ? "text-green-500" : "text-red-500"
            }`}
          >
            <AiOutlineCheckCircle className="mx-auto" size={50} />
            <h3 className="mt-4 text-lg font-semibold">{modalMessage}</h3>
            <Button onClick={handleCloseModal} className="mt-4">
              Close
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default ContactMandate;
